/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import DeleteIcon from "@material-ui/icons/Delete";
import ButtonWithLoading from "components/ButtonWithLoading";
import VisibilityButton from "components/VisibilityButton";
import apiRequest from "utils/apiRequestWithErrorCode";
import useClipboard from "utils/useClipboard";
import { CreateSmtpAuthUserResponse, SmtpAuthUser } from "@dashboard-v3/api";

const MAX_USERS = 2;

type UserEntry = {
  userName: string;
  password?: string;
  visible: boolean;
};

type State = {
  users: UserEntry[];
  isLoading: boolean;
  isCreating: boolean;
  deleteTarget: string | null;
  isDeleting: boolean;
};

function initState(): State {
  return {
    users: [],
    isLoading: true,
    isCreating: false,
    deleteTarget: null,
    isDeleting: false,
  };
}

export function SmtpAuthUsers() {
  const { t } = useTranslation("gateway");
  const { enqueueSnackbar } = useSnackbar();
  const copyToClipboard = useClipboard();
  const [state, setState] = useState<State>(initState);

  useEffect(() => {
    fetchUsers();
  }, []);

  function update(patch: Partial<State>) {
    setState(prev => ({ ...prev, ...patch }));
  }

  async function fetchUsers() {
    try {
      const users = await apiRequest<SmtpAuthUser[]>("GET", "/smtp-auth/users");
      update({
        users: users.map(u => ({ ...u, visible: false })),
        isLoading: false,
      });
    } catch {
      enqueueSnackbar(t("smtpAuth.fetch.error"), { variant: "error" });
      update({ isLoading: false });
    }
  }

  async function createUser() {
    update({ isCreating: true });
    try {
      const created = await apiRequest<CreateSmtpAuthUserResponse>(
        "POST",
        "/smtp-auth/users"
      );
      setState(prev => ({
        ...prev,
        isCreating: false,
        users: [...prev.users, { ...created, visible: true }],
      }));
    } catch {
      enqueueSnackbar(t("smtpAuth.create.error"), { variant: "error" });
      update({ isCreating: false });
    }
  }

  async function deleteUser() {
    const { deleteTarget } = state;
    if (!deleteTarget) return;

    update({ isDeleting: true });
    try {
      await apiRequest(
        "DELETE",
        `/smtp-auth/users/${encodeURIComponent(deleteTarget)}`
      );
      setState(prev => ({
        ...prev,
        isDeleting: false,
        deleteTarget: null,
        users: prev.users.filter(u => u.userName !== deleteTarget),
      }));
    } catch {
      enqueueSnackbar(t("smtpAuth.delete.error"), { variant: "error" });
      update({ isDeleting: false });
    }
  }

  function toggleVisibility(userName: string) {
    setState(prev => ({
      ...prev,
      users: prev.users.map(u =>
        u.userName === userName ? { ...u, visible: !u.visible } : u
      ),
    }));
  }

  const canAddUser = state.users.length < MAX_USERS;

  return (
    <>
      <Box style={{ marginBottom: 20, maxWidth: "60%" }}>
        <Typography variant="subtitle2">{t("smtpAuth.title")}</Typography>
        <Typography variant="caption" color="textSecondary">
          {t("smtpAuth.subtitle")}
        </Typography>
      </Box>

      {state.users.map(user => (
        <Box
          key={user.userName}
          display="flex"
          alignItems="center"
          style={{ gap: 10, marginBottom: 12 }}
        >
          <TextField
            variant="outlined"
            size="small"
            label={t("smtpAuth.field.username")}
            value={user.userName}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={t("smtpAuth.actions.copyUsername")}>
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(user.userName)}
                    >
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            style={{ width: "22%" }}
          />
          <TextField
            variant="outlined"
            size="small"
            label={t("smtpAuth.field.password")}
            value={user.password ?? "••••••••••••••••"}
            type={user.visible && user.password ? "text" : "password"}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end" style={{ gap: 4 }}>
                  {user.password && (
                    <Tooltip title={t("smtpAuth.actions.copyPassword")}>
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(user.password!)}
                      >
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <VisibilityButton
                    isVisible={user.visible}
                    onClick={() => toggleVisibility(user.userName)}
                  />
                </InputAdornment>
              ),
            }}
            style={{ width: "38%" }}
          />
          <Tooltip title={t("smtpAuth.actions.delete")}>
            <IconButton
              size="small"
              onClick={() => update({ deleteTarget: user.userName })}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ))}

      <ButtonWithLoading
        variant="contained"
        color="default"
        size="small"
        disableElevation
        disabled={!canAddUser || state.isLoading}
        loading={state.isCreating}
        onClick={createUser}
      >
        {t("smtpAuth.actions.addUser")}
      </ButtonWithLoading>

      <Dialog
        open={Boolean(state.deleteTarget)}
        onClose={() => update({ deleteTarget: null })}
      >
        <DialogTitle>{t("smtpAuth.deleteModal.title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("smtpAuth.deleteModal.body", { userName: state.deleteTarget })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => update({ deleteTarget: null })}
            disabled={state.isDeleting}
          >
            {t("smtpAuth.deleteModal.cancel")}
          </Button>
          <ButtonWithLoading
            variant="contained"
            color="secondary"
            disableElevation
            loading={state.isDeleting}
            onClick={deleteUser}
          >
            {t("smtpAuth.deleteModal.confirm")}
          </ButtonWithLoading>
        </DialogActions>
      </Dialog>
    </>
  );
}
